/*! purgecss start ignore - PurgeCSS needs to see these classes to know to keep them in the stylesheet */
// swiper-pagination-bullet
// swiper-pagination-bullet-active
// swiper-button-disabled
// swiper-button-prev:after
// swiper-button-next:after
/*! purgecss end ignore */

import React, { useContext, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { Swiper } from 'swiper'
import { Pagination, Navigation, A11y, Keyboard } from 'swiper/modules'
import { Swiper as SwiperCore } from 'swiper/types'
import { SwiperOptions } from 'swiper/types'
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import SliderNavigation from '@toasttab/shared/src/components/molecules/SliderNavigation/SliderNavigation'
import DynamicCards from '@toasttab/shared/src/components/molecules/CardTypes/DynamicCard'
import SliderPagination from '@toasttab/shared/src/components/molecules/SliderPagination/SliderPagination'
import { Text } from '@toasttab/shared/src/utils/craftUtils'
import { SliderProps } from './Slider.d'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import { Icon } from '@toasttab/shared/src/utils/craftUtils'
import { localizeLink } from '@toasttab/shared/src/utils/linkUtils'
import { I18nextContext } from '@toasttab/shared/src/context/i18nextContext'

const Slider = ({
  bgColor = 'bg-white',
  centerActiveSlide,
  cssClasses,
  fullWidth,
  dataTestId = 'Slider-1',
  initSlider = true,
  mobileMargin,
  pagination,
  previousNextButtons = 'null',
  prevNextClasses,
  scrollTrackingSectionName,
  sectionDescription,
  sectionHeading,
  link,
  // Setting as true by default for now to prevent breaking changes
  // Allows us to use other components in Slider, not just cards
  sliderCards = true, // todo - default this to false
  sliderCss,
  slideCss = '',
  sliderLoop,
  slides = [],
  slidesToShow = 1,
  hideSlider,
  textAlignment = 'left',
  textAlignmentMobile = 'left',
  toggleSwitch, // visiblePrevNextSlide
  toggleSwitch2, // showPartialSlidesMobile
  uniqueId
}: SliderProps) => {
  const [swiperSlider, setSwiperSlider] = useState<SwiperCore>(null)
  const [Slides, setSlides] = useState<any[]>([])
  const [ActiveIndex, setActiveIndex] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(true)
  const ref = useRef<any | null>(null)

  const context = useContext(I18nextContext)

  // Accessibility helper: Update slide visibility and focus management
  const updateSlideAccessibility = (swiper: SwiperCore) => {
    if (!swiper || !swiper.slides) return

    swiper.slides.forEach((slide: HTMLElement) => {
      const isVisible = slide.classList.contains('swiper-slide-visible')

      // Set aria-hidden based on visibility
      slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true')

      // Manage tabindex on the slide container itself
      if (isVisible) {
        // Make visible slides focusable in natural tab order
        slide.setAttribute('tabindex', '0')
      } else {
        // Prevent focus on hidden slide containers
        slide.setAttribute('tabindex', '-1')
      }

      // Remove Swiper's default role/aria-label from slides
      slide.removeAttribute('role')
      slide.removeAttribute('aria-label')

      // Manage focus for interactive elements within slides
      const focusableElements = slide.querySelectorAll(
        'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
      )

      focusableElements.forEach((element: Element) => {
        const htmlElement = element as HTMLElement
        if (isVisible) {
          // Make visible slide content focusable
          const currentTabIndex = htmlElement.getAttribute(
            'data-original-tabindex'
          )
          if (currentTabIndex) {
            htmlElement.setAttribute('tabindex', currentTabIndex)
          } else {
            htmlElement.removeAttribute('tabindex')
          }
        } else {
          // Store original tabindex and make hidden
          if (!htmlElement.hasAttribute('data-original-tabindex')) {
            const originalTabIndex = htmlElement.getAttribute('tabindex') || '0'
            htmlElement.setAttribute('data-original-tabindex', originalTabIndex)
          }
          htmlElement.setAttribute('tabindex', '-1')
        }
      })
    })
  }

  // Prevent Tab key from being processed by Swiper's keyboard module
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Tab') {
      e.stopPropagation()
    }
  }

  const singleSlide =
    slides && slides.length === 1 && Number(slidesToShow) === 1

  const initSwiper = () => {
    if (!loading) {
      return
    }

    setLoading(true)
    const swiperObj: SwiperOptions = {
      init: false,
      modules: [Pagination, Navigation, A11y, Keyboard],
      speed: 300,
      threshold: 30,
      grabCursor: true,
      spaceBetween: 16,
      centeredSlides: centerActiveSlide,
      watchSlidesProgress: true, // Critical: Enables swiper-slide-visible class
      a11y: {
        enabled: true,
        slideLabelMessage: `Slide {{index}} of {{slidesLength}}`,
        scrollOnFocus: false // Prevents auto-scrolling when tabbing through content
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
        pageUpDown: false // Prevents PageUp/PageDown from navigating slides
      },
      loop: sliderLoop,
      on: {
        afterInit: function (this: SwiperCore) {
          updateSlideAccessibility(this)
        },
        init: function (this: SwiperCore) {
          updateSlideAccessibility(this)
        },
        update: function (this: SwiperCore) {
          updateSlideAccessibility(this)
        },
        resize: function (this: SwiperCore) {
          updateSlideAccessibility(this)
        },
        slideChange: function (this: SwiperCore) {
          updateSlideAccessibility(this)
        }
      },
      enabled: slides?.length > 1 ? true : false,
      slidesPerView: toggleSwitch2 ? 1.25 : 1,
      observeParents: true,
      observeSlideChildren: true,
      updateOnWindowResize: true,
      breakpoints: {
        768: {
          slidesPerView: Number(slidesToShow),
          centeredSlides: false,
          spaceBetween: 24
        }
      }
    }

    if (pagination) {
      Object.assign(swiperObj, {
        pagination: {
          el: ref.current.querySelector(`.swiper-pagination`),
          clickable: false
        }
      })
    }

    if (previousNextButtons !== 'null') {
      Object.assign(swiperObj, {
        navigation: {
          nextEl: ref.current.querySelector(`.swiper-button-next`),
          prevEl: ref.current.querySelector(`.swiper-button-prev`)
        }
      })
    }

    const swiper = new Swiper(ref.current, swiperObj)

    swiper.on('init', function () {
      swiper.update()
      setActiveIndex(swiper.activeIndex)
    })

    swiper.on('slideChange', function () {
      setActiveIndex(swiper.activeIndex)
    })

    setSwiperSlider(swiper)

    setLoading(false)
  }

  useEffect(() => {
    setSlides(slides)
  }, [])

  useEffect(() => {
    if (Slides?.length > 0 && initSlider && !hideSlider) {
      initSwiper()
    }
  }, [Slides])

  useEffect(() => {
    if (swiperSlider) {
      swiperSlider.init()

      // Add keyboard event listener to prevent Tab from being processed by Swiper
      const sliderElement = ref.current
      if (sliderElement) {
        sliderElement.addEventListener('keydown', handleKeyDown)
      }

      // Cleanup
      return () => {
        if (sliderElement) {
          sliderElement.removeEventListener('keydown', handleKeyDown)
        }
      }
    }
  }, [swiperSlider])

  const sliderContainerClasses = classNames(
    cssClasses,
    bgColor,
    'mds-slider overflow-hidden',
    {
      'pt-[4.5rem]': !cssClasses,
      'pb-4': mobileMargin
    }
  )

  const cardTextAlign =
    Text.alignment(textAlignmentMobile) + ' sm:' + Text.alignment(textAlignment)
  const sliderClasses = classNames(
    sliderCss,
    'swiper mds-swiper mt-8 relative',
    {
      'md:mt-14': !link,
      '!overflow-visible': toggleSwitch
    }
  )

  if (slides && slides.length === 0) return null

  const containerClasses = classNames({
    'ds-container': !fullWidth,
    'pb-10 lg:pb-9': true
  })

  const sliderWrapperClasses = classNames('items-stretch', {
    'swiper-wrapper': !hideSlider,
    'md:justify-center': Slides?.length < 3 && slidesToShow !== 1,
    'mb-4': Slides?.length > Number(slidesToShow),
    'flex flex-wrap gap-[24px]': hideSlider && slidesToShow !== 1
  })

  const navigationClasses = classNames(prevNextClasses, {
    hidden: Slides?.length <= Number(slidesToShow),
    'hidden sm:flex': Slides?.length > Number(slidesToShow)
  })

  const slideClasses = classNames(
    slideCss,
    'swiper-slide !h-auto flex [&>div]:w-full [&>div]:h-full [&>a]:w-full [&>a]:h-full',
    {
      '[&:not(:last-child)]:mb-[40px] [&:not(:last-child)]:sm:mb-[96px]':
        hideSlider && slidesToShow === 1,
      'col-span-4 md:col-span-2 lg:col-span-4 md:!w-[31%]':
        hideSlider && slidesToShow === 3
    }
  )
  return (
    <section
      className={sliderContainerClasses}
      data-testid={dataTestId}
      data-tracking-section={scrollTrackingSectionName}
      id={uniqueId}
    >
      <div className='ds-wrapper overflow-hidden'>
        <div className={containerClasses}>
          <div className={cardTextAlign}>
            {sectionHeading ? (
              <Typography
                as='h2'
                classes='text-default sm:text-[2.8125rem]'
                variation='h2'
              >
                {sectionHeading}
              </Typography>
            ) : null}
            {sectionDescription ? (
              <Typography as='p' classes='mt-2 md:mt-4 text-gray-100 '>
                {sectionDescription}
              </Typography>
            ) : null}
          </div>
          {link ? (
            <div className={`${cardTextAlign} mt-8`}>
              <TextLink
                classes='!leading-[24px]'
                href={link.url}
                icon={Icon.linkIcons(link.icon)}
                text={link.label}
              />
            </div>
          ) : null}
          <div className={sliderClasses} ref={ref}>
            <div className={sliderWrapperClasses}>
              {Slides?.length > 0 &&
                Slides.map((Slide: any, i: number) => {
                  const active = ActiveIndex === i
                  const isPrevious = ActiveIndex - 1 === i
                  const isNext = ActiveIndex + Number(slidesToShow) === i
                  const slideCss = classNames(slideClasses, {
                    '[mask-image:linear-gradient(to_left,black,transparent_100px)]':
                      isPrevious && toggleSwitch && !hideSlider,
                    '[mask-image:linear-gradient(to_right,black,transparent_100px)]':
                      isNext && toggleSwitch && !hideSlider
                  })

                  // Create a copy of Slide without the properties we want to exclude
                  const { toggleSwitch2, ...slideWithoutToggleSwitch2 } = Slide
                  const slide = {
                    ...slideWithoutToggleSwitch2,
                    singleSlide
                  }

                  // Extract ctaLink and ctaLabel from products[0] to top level for InformationCard compatibility
                  if (slide.products && slide.products[0]) {
                    const { ctaLink, ctaLabel, ...productWithoutCta } =
                      slide.products[0]

                    // Add ctaLink and ctaLabel to top level of slide if they exist
                    if (ctaLink) {
                      slide.ctaLink = ctaLink
                    }
                    if (ctaLabel) {
                      slide.ctaLabel = ctaLabel
                    }

                    // Remove ctaLink and ctaLabel from products[0]
                    slide.products = [
                      { ...productWithoutCta },
                      ...slide.products.slice(1)
                    ]
                  }

                  // Check if slide should be a link (compatible with InformationCard logic)
                  // InformationCard uses: if (ctaLink && !ctaLabel) to make card an anchor
                  // We also support toggleSwitch2 for backward compatibility
                  const isLink =
                    slide.ctaLink && (Slide.toggleSwitch2 || !slide.ctaLabel)
                  const CardEl = isLink ? 'a' : 'div'

                  // Prevent nested anchors: if wrapper is anchor, don't pass ctaLink to InformationCard
                  // when !ctaLabel (so InformationCard won't also become an anchor)
                  const slideForCard = { ...slide }
                  if (isLink && slide.ctaLink && !slide.ctaLabel) {
                    delete slideForCard.ctaLink
                  }

                  return (
                    <CardEl
                      className={slideCss}
                      data-swiper-slide-index={i + 1}
                      key={`${Slide.sectionHeading}-${i}`}
                      tabIndex={-1}
                      href={
                        slide.ctaLink
                          ? localizeLink(
                              slide.ctaLink,
                              context.language,
                              context
                            )
                          : null
                      }
                    >
                      {sliderCards ? (
                        <DynamicCards
                          card={Slide.typeHandle}
                          {...slideForCard}
                          slideIsActive={active}
                        />
                      ) : (
                        React.cloneElement(Slide)
                      )}
                    </CardEl>
                  )
                })}
            </div>
            {previousNextButtons !== 'null' && !hideSlider && (
              <SliderNavigation
                classes={navigationClasses}
                previousNextButtons={previousNextButtons}
                swiper={swiperSlider}
              />
            )}
            {pagination ? <SliderPagination /> : null}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Slider
